import { type GetCustomerExperimentsResponse } from '@hungryroot/rest/api/v2/customers/[id]/experiments/get';
import { type ExperimentsResponse } from '@hungryroot/rest/api/v2/experiments/get';
import { type UseCustomerExperimentsType } from '../types';
import { useExperimentDefinitions } from './useExperimentDefinitions';

declare global {
  interface Window {
    [key: `exp_${number}`]: ExperimentVariantValue | undefined;
  }
}

type ExperimentVariantValue =
  GetCustomerExperimentsResponse['results'][number]['variant_modulo'];

type RequiredExperimentsType = Exclude<
  UseCustomerExperimentsType['data'],
  undefined
>;

function convertWindowExperimentsToCanonicalNumber({
  results: experiments,
}: ExperimentsResponse): RequiredExperimentsType {
  const bucketing: Record<
    string,
    RequiredExperimentsType['experiments'][number]
  > = {};
  experiments.forEach((experiment) => {
    const { is_pre_signup: isPreSignUp, id: experimentId } = experiment;
    if (isPreSignUp === false) {
      return;
    }

    const windowProperty = `exp_${Math.floor(experimentId / 100)}` as const;
    const value = window[windowProperty] as unknown;
    if (typeof value !== 'number') {
      return;
    }

    const bucketingValue: RequiredExperimentsType['experiments'][number] = {
      experiment: experimentId,
      variant: value,
      variant_modulo: (value % 10) as ExperimentVariantValue,
    };
    const existing = bucketing[windowProperty];
    if (existing === undefined) {
      bucketing[windowProperty] = bucketingValue;
      return;
    }

    const { experiment: existingExperimentNumber } = existing;
    if (existingExperimentNumber < experimentId) {
      bucketing[windowProperty] = bucketingValue;
    }
  });

  return {
    experiments: Object.values(bucketing),
  };
}

type Params = {
  enabled: boolean;
};

export function useAnonymousWindowExperiments({ enabled }: Params) {
  const { data: experimentDefinitionsData } = useExperimentDefinitions(
    {},
    {
      enabled,
    }
  );

  if (experimentDefinitionsData === undefined) {
    return {
      isLoading: true,
      error: null,
      isError: false,
      data: undefined,
    };
  }

  return {
    isLoading: false,
    error: null,
    isError: false,
    data: convertWindowExperimentsToCanonicalNumber(experimentDefinitionsData),
  };
}
